import React, { useState, useCallback } from "react";
import SelectSearch from "react-select";
import { flattenArray, formatFullName } from "../../../../lib";
import _debounce from "lodash.debounce";

function AttendeesTableFilters(props) {
  const {
    applyFilters,
    allEnrolledShluchim,
    allEnrolledShluchimLoading,
    allTourSchedules,
    allTracks,
    allVenues,
    isCheckInEnabled,
    isTravelTrip,
  } = props;

  const [keyword, setKeyword] = useState("");
  const [shliachId, setShliachId] = useState(null);
  const [campusId, setCampusId] = useState(null);
  const [statuses, setStatuses] = useState(["Registered"]);
  const [attendeeTypes, setAttendeeTypes] = useState([]);
  const [tourSchedules, setTourSchedules] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [venues, setVenues] = useState([]);

  const getFiltersObj = useCallback(
    () => ({
      attendeeTypes: attendeeTypes.map((t) => t.value),
      campusId: campusId || "",
      keyword,
      shliachId: shliachId || "",
      statuses,
      tourScheduleIds: tourSchedules.map((t) => t.value),
      trackIds: tracks.map((t) => t.value),
      venueIds: venues.map((v) => v.value),
    }),
    [
      attendeeTypes,
      campusId,
      keyword,
      shliachId,
      statuses,
      tourSchedules,
      tracks,
      venues,
    ],
  );

  const onFilterDebounce = useCallback(
    _debounce((val) => {
      onFilter("keyword", val);
    }, 500),
    [
      attendeeTypes,
      campusId,
      shliachId,
      statuses,
      tourSchedules,
      tracks,
      venues,
    ],
  );

  const onFilter = useCallback(
    (name, value) => {
      applyFilters({ ...getFiltersObj(), [name]: value });
    },
    [applyFilters, getFiltersObj],
  );

  const shluchimOptions =
    allEnrolledShluchim &&
    allEnrolledShluchim.map((s) => ({
      value: s.shliachID,
      label: formatFullName(s.shliachFirstName, s.shliachLastName),
    }));

  const campusOptions =
    allEnrolledShluchim &&
    flattenArray(allEnrolledShluchim.map((s) => s.enrolledCampuses)).map(
      (c) => ({
        value: c.campusID,
        label: c.campusName,
      }),
    );
  const allStatuses = ["Registered", "CheckedIn"];
  const allAttendeeTypes = [
    {
      value: "Shliach",
      label: "Shluchim",
    },
    {
      value: "Chaperone",
      label: "Chaperones",
    },
    {
      value: "Student",
      label: "Students",
    },
    {
      value: "OtherAttendee",
      label: "Other Attendees",
    },
  ];

  return (
    <div>
      <div className="trip-students-table-filter-inputs mb-16">
        <div className="search-input">
          <input
            type="text"
            placeholder={
              isTravelTrip
                ? "Search students"
                : "Search students, shluchim or chaperones"
            }
            name="keyword"
            onChange={(e) => {
              const _keyword = e.target.value;
              setKeyword(_keyword);
              onFilterDebounce(_keyword);
            }}
            value={keyword}
          />
          <i className="material-icons accent-text-secondary flex flex-align-center">
            search
          </i>
        </div>

        <SelectSearch
          className="select-search-input trip-filter-select"
          isClearable={true}
          isSearchable={true}
          onChange={(option) => {
            const _shliachId = option?.value;
            setShliachId(_shliachId);
            onFilter("shliachId", _shliachId);
          }}
          options={shluchimOptions}
          placeholder={
            allEnrolledShluchimLoading
              ? "Loading All Shluchim..."
              : "All Shluchim"
          }
          value={shliachId}
        />

        <SelectSearch
          className="trip-filter-select ml-16"
          isClearable={true}
          isSearchable={true}
          onChange={(option) => {
            const _campusId = option?.value;
            setCampusId(_campusId);
            onFilter("campusId", _campusId);
          }}
          options={campusOptions}
          placeholder={
            allEnrolledShluchimLoading
              ? "Loading All Campuses..."
              : "All Campuses"
          }
          value={campusId}
        />
      </div>

      <div
        className="trip-students-table-filter-inputs"
        style={{ flexWrap: "wrap" }}
      >
        {isCheckInEnabled && (
          <SelectSearch
            className="trip-filter-select mr-16 mb-16"
            isClearable={true}
            multi={true}
            searchable={false}
            onChange={(arrayStatuses) => {
              const arrayValues = arrayStatuses.map((s) => s.value);
              setStatuses(arrayValues);
              onFilter("statuses", arrayValues);
            }}
            options={allStatuses.map((status) => ({
              value: status,
              label: status,
            }))}
            placeholder="All Statuses"
            value={statuses}
          />
        )}

        {!isTravelTrip && (
          <>
            <SelectSearch
              className="trip-filter-select mr-16 mb-16"
              isClearable={true}
              multi={true}
              searchable={false}
              onChange={(arrayValues) => {
                setAttendeeTypes(arrayValues);
                onFilter(
                  "attendeeTypes",
                  arrayValues.map((s) => s.value),
                );
              }}
              options={allAttendeeTypes}
              placeholder="All Types"
              value={attendeeTypes}
            />

            <SelectSearch
              className="trip-filter-select mr-16 mb-16"
              isClearable={true}
              multi={true}
              searchable={false}
              onChange={(arrayValues) => {
                setTourSchedules(arrayValues);
                onFilter(
                  "tourScheduleIds",
                  arrayValues.map((s) => s.value),
                );
              }}
              options={
                allTourSchedules &&
                allTourSchedules.map((tourSchedule) => ({
                  value: tourSchedule.id,
                  label: tourSchedule.name,
                }))
              }
              placeholder="All Tour Schedules"
              value={tourSchedules}
            />
          </>
        )}

        <SelectSearch
          className="trip-filter-select mr-16 mb-16"
          isClearable={true}
          multi={true}
          searchable={false}
          onChange={(arrayValues) => {
            setTracks(arrayValues);
            onFilter(
              "trackIds",
              arrayValues.map((s) => s.value),
            );
          }}
          options={
            allTracks &&
            allTracks.map((track) => ({
              value: track.id,
              label: track.trackName,
            }))
          }
          placeholder="All Tracks"
          value={tracks}
        />

        {!isTravelTrip && (
          <SelectSearch
            className="trip-filter-select mr-16 mb-16"
            isClearable={true}
            multi={true}
            searchable={false}
            onChange={(arrayValues) => {
              setVenues(arrayValues);
              onFilter(
                "venueIds",
                arrayValues.map((v) => v.value),
              );
            }}
            options={allVenues.map((venue) => ({
              value: venue.id,
              label: venue.name,
            }))}
            placeholder="All Venues"
            value={venues}
          />
        )}
      </div>
    </div>
  );
}
export default React.memo(AttendeesTableFilters);
